import React, { useCallback, useEffect, useState } from "react";
import { TouchableOpacity, StyleSheet, Alert } from "react-native";
import { SafeAreaInsetsContext } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { isFactorTv } from "@rnv/renative";
import { useAuthStore } from "../state/authStore";

// The web/Tizen TV shell (TvApp) uses a plain nav object, not a
// NavigationContainer, so useNavigation() throws there. Kid mode is
// mobile-only and this badge renders nothing on TV, so swallow the throw
// and fall back to null — the hook is still called every render, keeping
// the hook order stable.
function useOptionalNavigation(): any {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNavigation<any>();
  } catch {
    return null;
  }
}
import { authApi } from "../api/auth";
import { Lock } from "lucide-react-native";
import PinGate from "./PinGate";
import { colors } from "../theme";


type Props = {
  // Optional hook so a screen can react to a successful exit (e.g. tear down
  // a running session). The lock will navigate to ParentHome regardless.
  onExit?: () => void;
};

/**
 * Floating lock badge that lets the parent exit kid-mode. The badge sits in
 * the top-right corner of whatever screen mounts it. Tapping the badge opens
 * the exit flow:
 *
 *  - If a Parent PIN is set, render the existing PinGate. On success, flip
 *    `kidMode` off and navigate back to ParentHome.
 *  - If no PIN is set, surface a confirmation prompt (the gate would be
 *    meaningless — `verifyPin` would throw "No PIN set" for any input).
 *
 * Mounted by every "kid surface" screen (ChildHome, PlayerScreen, etc.) and
 * renders nothing unless `kidMode === true`, so it's safe to drop into TV
 * screens too — they just won't see it.
 */
export default function KidModeLock({ onExit }: Props) {
  const navigation = useOptionalNavigation();
  const { kidMode, setKidMode, accessToken } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [hasPin, setHasPin] = useState<boolean | null>(null);
  // Push the badge below the status bar / notch (and clear of a side notch in
  // landscape). Read the context directly with a zero fallback so it's safe on
  // the TV web shell, which has no SafeAreaProvider.
  const insets = React.useContext(SafeAreaInsetsContext) ?? {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };

  // Refresh PIN status each time the exit flow opens — the parent might have
  // set a PIN in Me → PIN since the screen mounted.
  useEffect(() => {
    if (!open || !accessToken) return;
    let cancelled = false;
    authApi
      .pinStatus(accessToken)
      .then((r) => {
        if (!cancelled) setHasPin(r.hasPin);
      })
      .catch(() => {
        // Network error — fail open. Better to let the parent exit than to
        // strand them in kid mode because the API blipped.
        if (!cancelled) setHasPin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accessToken]);

  const finishExit = useCallback(() => {
    setOpen(false);
    setKidMode(false);
    onExit?.();
    // Reset the stack so a kid who later figures out the back gesture can't
    // walk back into ChildHome from the parent dashboard.
    navigation?.reset({ index: 0, routes: [{ name: "ParentHome" }] });
  }, [setKidMode, onExit, navigation]);

  const confirmNoPinExit = useCallback(() => {
    Alert.alert(
      "No PIN set",
      "Anyone can exit kid mode until you set a Parent PIN in Me → PIN. Exit anyway?",
      [
        { text: "Cancel", style: "cancel", onPress: () => setOpen(false) },
        { text: "Exit", style: "destructive", onPress: finishExit },
      ],
    );
  }, [finishExit]);

  // Surface the no-PIN confirmation as soon as we learn the status. Doing it
  // here (vs. inline in render) keeps the JSX clean and avoids re-firing the
  // Alert on every re-render.
  useEffect(() => {
    if (open && hasPin === false) confirmNoPinExit();
  }, [open, hasPin, confirmNoPinExit]);

  // Kid mode is a mobile-only flag — the TV is always a pure kid surface
  // and must never expose the parent-mode exit. Hide on TV regardless of
  // the (default-true) kidMode value.
  if (!kidMode || isFactorTv) return null;

  return (
    <>
      <TouchableOpacity
        style={[
          styles.badge,
          { top: insets.top + 8, right: insets.right + 14 },
        ]}
        onPress={() => {
          setHasPin(null);
          setOpen(true);
        }}
        activeOpacity={0.8}
        accessibilityLabel="Exit kid mode"
      >
        <Lock size={20} color="#fff" strokeWidth={2} />
      </TouchableOpacity>

      {open && hasPin === true && (
        <PinGate
          onSuccess={finishExit}
          onCancel={() => setOpen(false)}
          // Locked out: hand off straight into the password-based PIN reset
          // (forgot:true skips the current-PIN step), so a forgotten PIN
          // isn't a dead end.
          onForgot={() => {
            setOpen(false);
            navigation?.navigate("ChangePin", { forgot: true });
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  // Top-right corner. zIndex high enough to sit above video chrome and
  // ScrollView content. iOS safe-area roughly handled by `top: 16` — kid
  // mode screens are typically full-bleed, so a fixed top inset is fine.
  badge: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: colors.ink,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
});

// Re-export so callers can `import { KidModeLock } from ...`
export { KidModeLock };
